'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  BarChart3,
  Factory,
  FileSpreadsheet,
  Inbox,
  LayoutDashboard,
  Menu,
  Package,
} from 'lucide-react';
import { UserButton } from '@clerk/nextjs';

import { cn } from '@/lib/utils';
import { useIsAdmin } from '@/hooks/use-is-admin';
import { clearEmailListSnapshot } from '@/lib/emails/email-list-cache';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const navItems = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/emails', label: 'Emails', icon: Inbox },
  { href: '/inventory', label: 'Inventory', icon: Package },
];

const adminNavItems = [
  { href: '/admin/llm-costs', label: 'LLM Costs', icon: BarChart3 },
  // Stage 5: the shared correction screen (captain decision D1 - Admin > Onboarding).
  { href: '/admin/onboarding', label: 'Onboarding', icon: FileSpreadsheet },
];

const drawerLinkClasses =
  'flex min-h-[44px] items-center gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors';
const desktopLinkClasses =
  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors';

export function MainNav() {
  const pathname = usePathname();
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // M-44 (decision 1): a fresh nav to the inbox resets to top — clear any
  // restore snapshot so we never land deep in a stale filtered view. Only
  // return-from-detail (which does not pass through this link) restores.
  const handleNavClick = (href: string) => {
    if (href === '/emails') clearEmailListSnapshot();
  };

  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 border-b backdrop-blur">
      <div className="container flex h-14 items-center">
        {/* Mobile hamburger menu */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="mr-2 md:hidden"
              aria-label="Open navigation menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Factory className="text-primary h-5 w-5" aria-hidden="true" />
                Factory Automation
              </SheetTitle>
              <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
            </SheetHeader>
            <nav className="flex flex-col gap-1 px-2" aria-label="Mobile navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => {
                      setDrawerOpen(false);
                      handleNavClick(item.href);
                    }}
                    className={cn(
                      drawerLinkClasses,
                      isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              })}
              {isAdmin && !isAdminLoading && (
                <>
                  <div className="border-muted my-2 border-t" aria-hidden="true" />
                  <span className="text-muted-foreground px-4 text-[11px] tracking-widest uppercase">
                    Admin
                  </span>
                  {adminNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => {
                          setDrawerOpen(false);
                          handleNavClick(item.href);
                        }}
                        className={cn(
                          drawerLinkClasses,
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        )}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <Icon className="h-5 w-5" aria-hidden="true" />
                        {item.label}
                      </Link>
                    );
                  })}
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>

        <Link href="/" className="mr-8 flex items-center gap-2">
          <Factory className="text-primary h-6 w-6" aria-hidden="true" />
          <span className="hidden font-semibold sm:inline-block">Factory Automation</span>
        </Link>

        {/* Desktop navigation - hidden on mobile */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => handleNavClick(item.href)}
                className={cn(
                  desktopLinkClasses,
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
          {isAdmin && !isAdminLoading && (
            <>
              <div className="border-muted mx-3 h-5 border-l" aria-hidden="true" />
              <span className="text-muted-foreground mr-1 text-[11px] tracking-widest uppercase">
                Admin
              </span>
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      desktopLinkClasses,
                      isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="ml-auto">
          <UserButton />
        </div>
      </div>
    </header>
  );
}
